import styled from 'styled-components/native'
import { Trans } from 'react-i18next'
import I18n, { type TranslationKey } from 'APP/I18n'
import Card from 'APP/Converse/Card'
import Typography from 'APP/Converse/Typography'
import BulletListItem from 'APP/Components/BulletListItem'
import { CHECKOUT_TYPE } from '..'

const Container = styled.View`
  gap: ${({ theme }) => theme.metrics.baseMargin / 2}px;
`

interface BasePaymentTerms {
  lateCancellationAmount?: number | null
  noShowAmount?: number | null
}

interface PaymentTermsProps {
  checkoutType: CHECKOUT_TYPE
  termsContext: BasePaymentTerms
}

type PaymentTermKey = Extract<
  TranslationKey,
  `Checkout.PaymentTerms.${'appointment' | 'service'}.${string}`
>

type PaymentTerm = [PaymentTermKey, Record<string, unknown>?]

function getAppointmentPaymentTerms(ctx: BasePaymentTerms): PaymentTerm[] {
  return [
    ['Checkout.PaymentTerms.appointment.chargedAfterSession', undefined],
    ['Checkout.PaymentTerms.appointment.freeChanges', { window: 24 }],
    [
      'Checkout.PaymentTerms.appointment.lateCancellation',
      { fee: ctx.lateCancellationAmount, window: 24 },
    ],
    ['Checkout.PaymentTerms.appointment.noShow', { fee: ctx.noShowAmount }],
  ]
}

// TODO: This is unique to health panel at the moment. Ideally this is more dynamic.
// Discussions are being had to address how this can be accomplished longer term.
// See https://dialoguemd.atlassian.net/browse/CLI-615

function getServicePaymentTerms(): PaymentTerm[] {
  return [
    ['Checkout.PaymentTerms.service.chargedToday'],
    ['Checkout.PaymentTerms.service.fullRefundBefore'],
    ['Checkout.PaymentTerms.service.noRefundAfter'],
    ['Checkout.PaymentTerms.service.insuranceReceipt'],
  ]
}

export const PaymentTerms = ({ checkoutType, termsContext }: PaymentTermsProps) => {
  const terms =
    checkoutType === CHECKOUT_TYPE.APPOINTMENT
      ? getAppointmentPaymentTerms(termsContext)
      : getServicePaymentTerms()

  return (
    <Container testID="PAYMENT_TERMS_CONTAINER">
      <Typography variant="bodyLargeBold">{I18n.t('Checkout.PaymentTerms.title')}:</Typography>
      <Card testID="PAYMENT_TERMS_CARD">
        {terms.map(([tk, tv]) => (
          <BulletListItem key={tk}>
            <Typography variant="bodyNormal">
              <Trans
                i18n={I18n}
                i18nKey={tk}
                values={tv}
                components={[<Typography variant="bodyNormalBold" key="bold" />]}
              />
            </Typography>
          </BulletListItem>
        ))}
      </Card>
    </Container>
  )
}
